/**
 * @author Mukesh kumar bairwa <mukesh.bairwa@314ecorp.com>
 * @description Raqb config
 */

import React from 'react';
import {
	AntdConfig,
	ButtonProps,
	Config,
	ConfigContext,
	CoreConjunctions,
	Fields,
	Operators,
	Settings,
	Types,
	Widgets,
} from '@react-awesome-query-builder/antd';
import _ from 'lodash';
import { Tag } from 'antd';
import { Close } from '@icon-park/react';

import RenderButtons from '../component/RenderButtons';

export const OPERATORS = {
	equal: 'equal',
	notEqual: 'not_equal',
	less: 'less',
	lessOrEqual: 'less_or_equal',
	greater: 'greater',
	greaterOrEqual: 'greater_or_equal',
	like: 'like',
	notLike: 'not_like',
	startsWith: 'starts_with',
	endsWith: 'ends_with',
	between: 'between',
	notBetween: 'not_between',
	isNull: 'is_null',
	isNotNull: 'is_not_null',
	isEmpty: 'is_empty',
	isNotEmpty: 'is_not_empty',
	selectEquals: 'select_equals',
	selectNotEquals: 'select_not_equals',
	selectAnyIn: 'select_any_in',
	selectNotAnyIn: 'select_not_any_in',
	multiselectContains: 'multiselect_contains',
	multiselectNotContains: 'multiselect_not_contains',
	multiselectEquals: 'multiselect_equals',
	multiselectNotEquals: 'multiselect_not_equals',
};

const getConfig = (fields: Fields, dateFormat: string, entityType?: string): Config => {
	const settings: Settings = {
		...AntdConfig.settings,
		addRuleLabel: 'Add Filter',
		addGroupLabel: 'Add Group',
		canReorder: false,
		defaultConjunction: 'AND',
		forceShowConj: true,
		groupActionsPosition: 'bottomLeft',
		maxNesting: 2,
		renderSize: 'medium',
		showNot: false,
		canCompareFieldWithField: () => false,
		customFieldSelectProps: {
			size: 'large',
			style: {
				width: '200px',
			},
			showSearch: true,
			optionFilterProp: 'label',
		},
		customOperatorSelectProps: {
			size: 'large',
			style: {
				width: '120px',
			},
			showSearch: true,
			optionFilterProp: 'label',
		},
		renderButton: (props: ButtonProps, { RCE, W: { Button } }: ConfigContext) => {
			if (props) {
				return <RenderButtons {...props} />;
			}

			return RCE(Button, props);
		},
		renderValueSources: () => React.createElement('div'),
		removeIncompleteRulesOnLoad: false,
		renderConjs: ({ selectedConjunction }) =>
			selectedConjunction !== 'OR' && (
				<Tag color='#F0F7FF' style={{ color: '#2759CD', borderColor: '#A2C2F2' }}>
					{selectedConjunction}
				</Tag>
			),
	};

	const operators: Operators = {
		...AntdConfig.operators,
		[OPERATORS.equal]: {
			...AntdConfig.operators.equal,
			label: 'Is',
			formatOp: (field, __, value, ___, valueType) => {
				if (valueType === 'date') {
					return `whose ${_.toLower(field)} is on ${value}`;
				}
				return `whose ${_.toLower(field)} is ${value}`;
			},
		},
		[OPERATORS.notEqual]: {
			...AntdConfig.operators.not_equal,
			label: 'Is Not',
			formatOp: (field, __, value) => {
				return `whose ${_.toLower(field)} is not ${value}`;
			},
		},
		[OPERATORS.like]: {
			...AntdConfig.operators.like,
			label: 'Is',
			formatOp: (field, __, value) => {
				return `whose ${_.toLower(field)} is like ${value}`;
			},
		},
		[OPERATORS.less]: {
			...AntdConfig.operators.less,
			label: 'Less Than',
			formatOp: (field, __, value, ___, valueType) => {
				if (valueType === 'date') {
					return `whose ${_.toLower(field)} is before ${value}`;
				}
				return `whose ${_.toLower(field)} is less than ${value}`;
			},
		},
		[OPERATORS.lessOrEqual]: {
			...AntdConfig.operators.less_or_equal,
			label: 'Less Than or Equals',
			formatOp: (field, __, value, ___, valueType) => {
				if (valueType === 'date') {
					return `whose ${_.toLower(field)} is on or before ${value}`;
				}
				return `whose ${_.toLower(field)} is less than or equal to ${value}`;
			},
		},
		[OPERATORS.greater]: {
			...AntdConfig.operators.greater,
			label: 'Greater Than',
			formatOp: (field, __, value, ___, valueType) => {
				if (valueType === 'date') {
					return `whose ${_.toLower(field)} is after ${value}`;
				}
				return `whose ${_.toLower(field)} is more than ${value}`;
			},
		},
		[OPERATORS.greaterOrEqual]: {
			...AntdConfig.operators.greater_or_equal,
			label: 'Greater Than or Equals',
			formatOp: (field, __, value, ___, valueType) => {
				if (valueType === 'date') {
					return `whose ${_.toLower(field)} is on or after ${value}`;
				}
				return `whose ${_.toLower(field)} is greater than or equal to ${value}`;
			},
		},
		[OPERATORS.multiselectContains]: {
			...AntdConfig.operators.multiselect_contains,
			label: 'Is',
			formatOp: (field, __, value) => {
				if (_.isArray(value)) {
					return `whose ${_.toLower(field)} is either of [${value.join(', ')}]`;
				}
				return `whose ${_.toLower(field)} is ${value}`;
			},
		},
		[OPERATORS.multiselectNotContains]: {
			...AntdConfig.operators.multiselect_not_contains,
			label: 'Is Not',
			formatOp: (field, __, value) => {
				const valueString = _.isArray(value) ? value.join(', ') : value;
				return `whose ${_.toLower(field)} is not in ${valueString}`;
			},
		},
		[OPERATORS.between]: {
			...AntdConfig.operators.between,
			label: 'Is Between',
			formatOp: (field, __, value) => {
				const [start, end] = value;
				return `whose ${_.toLower(field)} is from ${start} to ${end}`;
			},
		},
	};

	const defaultWidgetProps = {
		fullWidth: true,
		customProps: {
			size: 'large',
			style: {
				width: '100%',
			},
		},
	};

	const widgets: Widgets = {
		...AntdConfig.widgets,
		text: {
			...AntdConfig.widgets.text,
			...defaultWidgetProps,
			formatValue: (val, __, wgtDef, isForDisplay) => {
				if (isForDisplay) {
					const renderer = wgtDef.customProps?.stringifiedValueRenderer;
					if (typeof renderer === 'function') {
						return renderer(val);
					}
					return val;
				}
				return val;
			},
		},
		number: {
			...AntdConfig.widgets.number,
			...defaultWidgetProps,
			formatValue: (val, __, wgtDef, isForDisplay) => {
				if (isForDisplay) {
					const renderer = wgtDef.customProps?.stringifiedValueRenderer;
					if (typeof renderer === 'function') {
						return renderer(val);
					}
					return val;
				}
				return val;
			},
		},
		date: {
			...AntdConfig.widgets.date,
			...defaultWidgetProps,
			dateFormat,
		},
		datetime: {
			...AntdConfig.widgets.datetime,
			...defaultWidgetProps,
			dateFormat,
		},
		select: {
			...AntdConfig.widgets.select,
			...defaultWidgetProps,
		},
		multiselect: {
			...AntdConfig.widgets.multiselect,
			...defaultWidgetProps,
			customProps: {
				...defaultWidgetProps.customProps,
				maxTagCount: 1,
				allowClear: { clearIcon: <Close /> },
			},
		},
		treemultiselect: {
			...AntdConfig.widgets.treemultiselect,
			...defaultWidgetProps,
		},
		treeselect: {
			...AntdConfig.widgets.treeselect,
			...defaultWidgetProps,
		},
	};

	const types: Types = {
		...AntdConfig.types,
		text: {
			...AntdConfig.types.text,
			widgets: {
				text: {
					operators: [OPERATORS.like],
				},
			},
			defaultOperator: OPERATORS.like,
		},
		select: {
			...AntdConfig.types.select,
			widgets: {
				select: {
					operators: [OPERATORS.equal, OPERATORS.notEqual],
				},
			},
			defaultOperator: OPERATORS.equal,
		},
		date: {
			...AntdConfig.types.date,
			widgets: {
				date: {
					operators: [OPERATORS.equal, OPERATORS.notEqual, OPERATORS.greater, OPERATORS.less, OPERATORS.between],
				},
			},
			defaultOperator: OPERATORS.equal,
		},
		number: {
			...AntdConfig.types.number,
			widgets: {
				number: {
					operators: [
						OPERATORS.equal,
						OPERATORS.notEqual,
						OPERATORS.greater,
						OPERATORS.less,
						OPERATORS.between,
						OPERATORS.greaterOrEqual,
						OPERATORS.lessOrEqual,
					],
				},
			},
			defaultOperator: OPERATORS.equal,
		},
		multiselect: {
			...AntdConfig.types.multiselect,
			widgets: {
				multiselect: {
					operators: [OPERATORS.multiselectContains, OPERATORS.multiselectNotContains],
				},
			},
			defaultOperator: OPERATORS.multiselectContains,
		},
		treemultiselect: {
			...AntdConfig.types.treemultiselect,
			widgets: {
				treemultiselect: {
					operators: [OPERATORS.multiselectContains, OPERATORS.multiselectNotContains],
				},
			},
			defaultOperator: OPERATORS.multiselectContains,
		},
		treeselect: {
			...AntdConfig.types.treeselect,
			widgets: {
				treeselect: {
					operators: [OPERATORS.equal, OPERATORS.notEqual],
				},
			},
			defaultOperator: OPERATORS.equal,
		},
	};
	const conjunctions: CoreConjunctions = {
		...AntdConfig.conjunctions,
		OR: {
			...AntdConfig.conjunctions.OR,
			label: 'Or',
			formatConj: (children) => {
				return children.join('{OR}');
			},
		},
		AND: {
			...AntdConfig.conjunctions.AND,
			label: 'And',
			formatConj: (children) => {
				return `All ${entityType ?? 'items'} ${children.join(' and ')}`;
			},
		},
	};
	return {
		...AntdConfig,
		conjunctions,
		operators,
		widgets,
		types,
		fields,
		settings,
	};
};

export { getConfig };
